import { type ReactNode } from "react";
import { StatusBadge, defaultNumberFormat } from "../../components/index.ts";
import type { IdentityMcpSummary } from "./use-identity-mcp.ts";

/**
 * The opt-in coverage banner (task 7.3; requirement 13.3 "opt-in coverage banner, dark = off on
 * purpose").
 *
 * Identity MCP is opt-in: a source reports only when deliberately configured on, and a source that
 * is registered but reports nothing is "dark on purpose", not broken (requirements glossary). The
 * banner reads the three honest counts from the summary's `opt_in` block — registered, reporting and
 * dark — and frames the dark count NEUTRALLY: it is normal, not an error. The banner only takes on
 * an informational tone when some registered sources are dark (so an operator sees coverage at a
 * glance) and a healthy tone when every registered source is reporting.
 *
 * The three counts are exact catalog `count`s (non-negative integers), so no exact/estimate badge is
 * needed here. Status is conveyed by a `StatusBadge` (icon + text) plus the explanatory sentence,
 * never by colour alone (design 12.4).
 */

type OptIn = IdentityMcpSummary["opt_in"];

export function OptInBanner(props: { optIn: OptIn }): ReactNode {
  const { optIn } = props;
  const registered = optIn.sources_registered;
  const reporting = optIn.sources_reporting;
  const dark = optIn.sources_dark;

  // The banner's framing: all-reporting is healthy; some-dark is informational (not an error);
  // nothing registered is a neutral "no coverage configured" note.
  const kind: "none" | "all" | "partial" =
    registered === 0 ? "none" : dark === 0 ? "all" : "partial";

  const { level, label, blurb } = framing(kind, registered, reporting, dark);

  return (
    <section
      className="imcp-optin"
      data-kind={kind}
      aria-label="Identity MCP opt-in coverage"
    >
      <div className="imcp-optin__head">
        <StatusBadge level={level} label={label} />
        <p className="imcp-optin__blurb">{blurb}</p>
      </div>
      <ul className="imcp-optin__counts">
        <CoverageCount label="Registered" value={registered} hint="Sources known to the tenant." />
        <CoverageCount
          label="Reporting"
          value={reporting}
          hint="Registered sources that reported Identity MCP telemetry in this window."
        />
        <CoverageCount
          label="Dark"
          value={dark}
          hint="Registered but not reporting — off on purpose, not broken."
          muted
        />
      </ul>
    </section>
  );
}

function framing(
  kind: "none" | "all" | "partial",
  registered: number,
  reporting: number,
  dark: number,
): { level: "healthy" | "neutral"; label: string; blurb: string } {
  switch (kind) {
    case "none":
      return {
        level: "neutral",
        label: "No sources registered",
        blurb:
          "No Identity MCP sources are registered for this tenant. Identity MCP is opt-in; register and enable a source to see its operations here.",
      };
    case "all":
      return {
        level: "healthy",
        label: "All sources reporting",
        blurb: `All ${defaultNumberFormat(registered)} registered Identity MCP source(s) are reporting in this window.`,
      };
    case "partial":
      return {
        level: "neutral",
        label: `${defaultNumberFormat(dark)} dark on purpose`,
        blurb: `${defaultNumberFormat(reporting)} of ${defaultNumberFormat(registered)} registered source(s) are reporting; ${defaultNumberFormat(dark)} are dark — opt-in means a registered source reports only when deliberately configured on, so this is expected, not a fault.`,
      };
  }
}

function CoverageCount(props: {
  label: string;
  value: number;
  hint: string;
  muted?: boolean;
}): ReactNode {
  const display = defaultNumberFormat(props.value);
  return (
    <li
      className="imcp-optin__count"
      data-muted={props.muted === true ? "true" : undefined}
      aria-label={`${props.label}: ${display}. ${props.hint}`}
    >
      <span className="imcp-optin__count-value">{display}</span>
      <span className="imcp-optin__count-label">{props.label}</span>
      <span className="imcp-optin__count-hint">{props.hint}</span>
    </li>
  );
}
